import type { H3Event } from 'h3'
import type { Ref } from 'vue'

/**
 * Edge cache for a collection URL that names nothing in the catalogue.
 *
 * Shorter than the hour the `/{locale}/collections/**` route rule gives a real
 * listing (`HTML_CACHE_CONTROL` in nuxt.config.ts), which Nitro applies before
 * rendering and so whatever the status. A cached 404 carries no `c:<id>` tag —
 * there is no collection to name — so it is purged when a collection is created
 * or renamed (`nav:<locale>`), but not when an existing one is later assigned to
 * this locale's channel. Five minutes bounds that gap, and still lets the edge
 * absorb a crawler walking made-up URLs instead of SSR rendering every one.
 */
const NOT_FOUND_CACHE_CONTROL = 'public, max-age=0, s-maxage=300, stale-while-revalidate=60'

/** Replaces the route rule's header for this response, as `plugins/3.urlCoupon.ts` does. */
function overrideCacheControl(event: H3Event | undefined, value: string): void {
  if (event?.node?.res && !event.node.res.headersSent)
    event.node.res.setHeader('Cache-Control', value)
}

/**
 * Makes sure an awaited lookup has actually run before a page decides 404 from it.
 *
 * When a client-side navigation starts while Nuxt is still hydrating, a
 * `useAsyncData` with no payload of its own defers its fetch to `onBeforeMount`
 * (nuxt `app/composables/asyncData.js`), so `await` returns with nothing settled
 * and a setup-time "no such collection" would be read off a lookup that never
 * happened — a real collection rendered as the 404. The lookup must have no
 * `default`, so that `undefined` can only mean "not run"; its handler answers
 * `null` for "not found".
 */
export async function settleCollectionLookup(lookup: {
  data: Ref<unknown>
  error: Ref<unknown>
  refresh: () => Promise<unknown>
}): Promise<void> {
  if (import.meta.client && lookup.data.value === undefined && !lookup.error.value)
    await lookup.refresh()
}

/**
 * A collection route whose URL value names nothing — no Collection with that
 * slug, no facet value with that code, no colour bucket with that key.
 *
 * It answers like every other dead URL on the site: 404, `noindex, nofollow`,
 * and the shared 404 template (the calling page renders `<LazyPageNotFound />`).
 * The title is the 404's own, never text made from the URL — echoing a made-up
 * slug back as an indexable page is what let anyone publish spam on this domain
 * by linking to `/collections/<anything>`.
 *
 * `useSeoMeta`, not the shared `setSeoMeta`: that helper always writes a
 * canonical and every locale's hreflang alternate, even under `noindex`, which
 * on a dead URL would advertise it in five languages. The content catch-all
 * (`pages/[...slug].vue`) leaves its 404 head the same way.
 *
 * Call it once, after the lookup has been awaited, only when it found nothing.
 */
export function useCollectionNotFound(): void {
  const { t } = useI18n()
  if (import.meta.server) {
    setResponseStatus(404)
    overrideCacheControl(useRequestEvent(), NOT_FOUND_CACHE_CONTROL)
  }
  useSeoMeta({
    title: () => t('layouts.title', [t('error.404.title')]),
    robots: 'noindex, nofollow',
  })
}

/**
 * A lookup that FAILED, which is not one that found nothing.
 *
 * Read as "not found", a Vendure blip would 404 a real collection, and the
 * route rule would have the edge serve that 404 for an hour and more. So it is
 * a 503 the edge must not keep (`private, no-store`), and a full error page
 * rather than a listing with no products, as `pages/models/[key].vue` does for
 * its lookup.
 *
 * Thrown on the server, which is what gives the response its status. On the
 * client it is shown instead (`fatal`, so the error page takes over), and the
 * page's setup runs on: a throw there stopped setup halfway, and the page's
 * template then rendered once against bindings that were never declared —
 * a TypeError on every in-app 503.
 */
export function failCollectionLookup(): void {
  const error = createError({ statusCode: 503, statusMessage: 'Collection temporarily unavailable', fatal: true })
  if (import.meta.server) {
    overrideCacheControl(useRequestEvent(), 'private, no-store')
    throw error
  }
  showError(error)
}
